import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import type { RedeemInviteResponse } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { useSession } from "../../lib/session.tsx";
import { accountErrorText } from "../auth/auth-errors.ts";
import { AdoptConfirm } from "./AdoptConfirm.tsx";
import { type ConflictScreen, conflictScreenOn, deviceStore, scopeSummary } from "./adopt-texts.ts";
import { redeemedMe } from "./redeemed-me.ts";

/**
 * Погашение постоянной ссылки ученика.
 *
 * Ссылка не создаёт ученика, она ОТДАЁТ уже заведённого преподавателем
 * человека тому, кто по ней пришёл (раздел 5.10 CLAUDE.md). Отсюда следствие,
 * которое видно прямо на этом экране: имя приходит с сервера, его задал
 * преподаватель, и ученик его здесь подтверждает, а не вводит. Ручки смены
 * отображаемого имени на M6 нет.
 *
 * Повторный заход по той же ссылке с той же cookie это просто вход. С другого
 * устройства без cookie это новая сессия на того же пользователя, и это
 * ожидаемое поведение: ссылка и есть его ключ, пока он не привязал вход.
 *
 * Ссылка второго преподавателя при живой сессии другого ученика ведёт не в
 * тупик «Вы уже вошли», а к вопросу «это один ученик?» (`AdoptConfirm`, раздел 9
 * `docs/account-adopt-design.md`), если сервер его предложил.
 */
export function JoinPage() {
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { adopt, signOut } = useSession();

  const [redeemed, setRedeemed] = useState<RedeemInviteResponse | null>(null);
  const [conflict, setConflictState] = useState<{
    info: api.AlreadySignedIn;
    screen: ConflictScreen;
  } | null>(null);
  /**
   * Какой экран рисовать на 409, решается здесь, один раз на ответ погашения,
   * а не на рендере (`conflictScreenOn`).
   */
  const setConflict = useCallback((info: api.AlreadySignedIn | null) => {
    setConflictState(info === null ? null : { info, screen: conflictScreenOn(deviceStore(), info) });
  }, []);
  const [err, setErr] = useState<api.ApiError | Error | null>(null);
  const [busy, setBusy] = useState(true);
  /**
   * Номер погашения: ключ экрана вопроса. Повторное погашение (после
   * `adopt_stale`) приносит новый 409, и экран обязан начаться с вопроса, а не
   * с прошлого отказа.
   */
  const [attempt, setAttempt] = useState(0);
  /**
   * Набор этого браузера вырос не самим погашением, а подтверждением
   * встречного переноса (`same_user`, 9.2): тогда погашение отвечает
   * `scopeGrew: false`, а итог на приветствии всё равно нужен.
   */
  const grewByAdopt = useRef(false);
  // StrictMode в деве монтирует эффект дважды. Погашение это не чистое чтение:
  // оно двигает счётчики использования ссылки и заводит сессию
  const started = useRef(false);

  const redeem = useCallback(async () => {
    setBusy(true);
    setErr(null);
    setAttempt((n) => n + 1);
    try {
      const res = await api.redeemInvite(code);
      if (res.kind === "conflict") {
        setConflict(res.info);
      } else {
        setConflict(null);
        setRedeemed(res.session);
      }
    } catch (e) {
      setErr(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }, [code, setConflict]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void redeem();
  }, [redeem]);

  /**
   * «Войти на занятие» с экрана «у ученика ссылки сейчас идёт занятие» (9.3):
   * выход, погашение и сразу в урок, который идёт. Урока уже нет (признак
   * отстаёт до 15 минут), значит в кабинет.
   */
  async function leaveToLesson(): Promise<void> {
    await signOut();
    setBusy(true);
    try {
      const res = await api.redeemInvite(code);
      if (res.kind === "conflict") {
        setConflict(res.info);
        return;
      }
      setConflict(null);
      adopt(redeemedMe(res.session));
      let live: string | null = null;
      try {
        const list = await api.listLessons({ part: "active" });
        live = list.lessons.find((l) => l.status === "live")?.id ?? null;
      } catch {
        /* список не пришёл: вход состоялся, остаётся кабинет */
      }
      void navigate(live === null ? "/" : `/lesson/${live}`, { replace: true });
    } catch (e) {
      setConflict(null);
      setErr(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }

  function accept(session: RedeemInviteResponse) {
    // Поля сессии собирает общий помощник: разбор, какие и почему, там
    adopt(redeemedMe(session));
    // replace: ссылка это долгоживущий секрет, и оставлять её в истории
    // браузера общего компьютера незачем
    void navigate("/", { replace: true });
  }

  if (busy) {
    return (
      <main className="lobby">
        <h1>Урок</h1>
        <p className="muted">Проверяем ссылку…</p>
      </main>
    );
  }

  if (conflict) {
    const offer = conflict.info.adopt;
    if (offer !== undefined && conflict.screen !== "old") {
      return (
        <AdoptConfirm
          key={attempt}
          code={code}
          info={{ ...conflict.info, adopt: offer }}
          onLeave={async () => {
            await signOut();
            await redeem();
          }}
          onLeaveToLesson={leaveToLesson}
          onRetry={({ grew }) => {
            grewByAdopt.current = grew;
            void redeem();
          }}
          onError={(e) => {
            setConflict(null);
            setErr(e);
          }}
        />
      );
    }
    return (
      <main className="lobby">
        <h1>Вы уже вошли</h1>
        <p>
          Эта ссылка ведёт к ученику «{conflict.info.studentDisplayName}» преподавателя «
          {conflict.info.teacherDisplayName}», а в этом браузере открыт другой вход.
        </p>
        {/* Молча подменять сессию нельзя: ссылку часто открывают из вкладки
            преподавателя, чтобы проверить, что она работает */}
        <div className="lobby__row">
          <button
            className="btn"
            onClick={() =>
              void (async () => {
                await signOut();
                await redeem();
              })()
            }
          >
            Выйти и войти как ученик
          </button>
          {/*
            ⚠️ Акцента на этом экране нет намеренно, хотя на соседних он есть.
            Здесь два ЗАКОННЫХ исхода, а не один: ссылку часто открывает сам
            преподаватель из своей вкладки, чтобы проверить, что она работает,
            и для него верный ответ это «Остаться». Заливка на «Выйти и войти»
            выбивала бы преподавателя из его же сессии одним привычным нажатием
            по самой заметной кнопке. Поэтому подтверждение обычного веса, а
            отмена тише него: пара различима, но никого не подталкивает.
          */}
          <button className="btn btn--quiet" onClick={() => void navigate("/", { replace: true })}>
            Остаться
          </button>
        </div>
      </main>
    );
  }

  if (err) {
    const code404 = err instanceof api.ApiError ? err.code : null;
    return (
      <main className="lobby">
        <h1>Ссылка не сработала</h1>
        <p className="err">
          {code404 === "invite_not_found"
            ? "Такой ссылки нет или она отозвана. Попросите преподавателя выдать новую."
            : code404 === "invite_locked" || code404 === "rate_limited"
              ? "Слишком много попыток. Подождите час и повторите."
              : ((err instanceof api.ApiError ? accountErrorText(err.code) : null) ?? err.message)}
        </p>
        <div className="lobby__row">
          {/*
            «Повторить» тихая, хотя она тут единственная. Обе названные причины
            отказа («ссылки нет», «слишком много попыток») повтором не лечатся:
            при `invite_not_found` нажимать бессмысленно, при `rate_limited`
            вредно, там прямо сказано ждать час. Главное на этом экране это
            текст с указанием попросить новую ссылку, и кнопка не должна
            перетягивать внимание на бесполезное действие.
          */}
          <button className="btn btn--quiet" onClick={() => void redeem()}>
            Повторить
          </button>
        </div>
      </main>
    );
  }

  if (redeemed) {
    /**
     * Итог «Теперь в этом браузере занятия у …» только если набор этим
     * погашением ВЫРОС (9.5, ревью 3 продукта, М7): у обычного первого входа
     * он сказал бы очевидное.
     */
    const summary =
      redeemed.scopeGrew === true || grewByAdopt.current
        ? scopeSummary(redeemed.scopeTeachers ?? [])
        : null;
    return (
      <main className="lobby">
        {/*
          Имя на паре ЭТОЙ ссылки, а не профильное (9.5): его вписал этот
          преподаватель, и держатель его ключа читает именно его. Старый API
          поля не пришлёт, тогда как раньше
        */}
        <h1>Здравствуйте, {redeemed.pairDisplayName ?? redeemed.displayName}</h1>
        <p className="muted">
          Преподаватель «{redeemed.teacherDisplayName}» пригласил вас на занятия. Имя задано им; если
          оно неверное, скажите об этом на уроке.
        </p>
        {summary !== null && <p>{summary}</p>}
        <div className="lobby__row">
          {/*
            ⚠️ Единственная заливка акцентом на всём пути входа по ссылке, и
            она здесь не для красоты. Это первый экран, который ученик видит от
            нас, и цена непонимания тут выше, чем где-либо: не понял, куда
            нажать, и урок сорван, а виноваты мы, а не он. На экране ровно один
            путь дальше, у кнопки нет соперников, и вес обязан это сказать
            прямо, не заставляя читать три абзаца, чтобы найти действие.
            Аудитория тут в том числе школьники и иностранцы на РКИ, у которых
            интерфейс на неродном языке, и заметная кнопка читается быстрее
            текста при любом уровне владения.
          */}
          <button className="btn btn--primary" onClick={() => accept(redeemed)}>
            Это я, продолжить
          </button>
          {/*
            Проверка звука ТИХОЙ кнопкой и РЯДОМ, а не вместо (правило 4.1
            `docs/room-preflight-design.md`). Проверка не встаёт между ссылкой и
            уроком: сорванный вход хуже плохого звука, поэтому единственная
            заливка акцентом на этом экране остаётся у «Это я, продолжить».

            ⚠️ Адрес возврата это текущий путь, то есть та же самая ссылка, по
            которой человек уже пришёл. Секрет от этого никуда не уезжает: он и
            так лежит в адресной строке и в истории этого браузера, а в заметку
            о неточности строка запроса не попадает вовсе (`maskRoute` режет её
            в `packages/shared/src/feedback.ts`).
          */}
          <button
            className="btn btn--quiet"
            onClick={() =>
              void navigate(`/check?next=${encodeURIComponent(location.pathname)}`)
            }
          >
            Проверить звук
          </button>
        </div>
        {/*
          ⚠️ Фраза для всех, а не только до привязки входа (9.5 и 18
          `docs/account-adopt-design.md`). С наборами пар ссылка открывает
          занятия у ЭТОГО преподавателя, и прежнее «единственный ключ к истории
          уроков» стало неправдой дважды: ключей бывает несколько, и каждый
          открывает свою часть
        */}
        <p className="muted">
          Сохраните эту ссылку: это ключ к занятиям у преподавателя «{redeemed.teacherDisplayName}».
          {summary !== null && " Ссылки от других преподавателей тоже сохраните."}
        </p>
      </main>
    );
  }

  return null;
}
